import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { FiX, FiSearch, FiCheck, FiSend, FiRefreshCw } from 'react-icons/fi';
import { FaCreditCard } from 'react-icons/fa';
import styled, { keyframes } from 'styled-components';
import api from '../../../../../../services/api';
import { useToast } from '../../../../../../hooks/Toast';
import { useModal } from '../../../../../../hooks/Modal';
import { useAuth } from '../../../../../../hooks/Auth';
import { useSearch } from '../../../../../../hooks/Search';

// =========================================================
// Endpoints (mesma convenção do módulo hoje — singular)
// =========================================================
const LIST_ENDPOINT = '/sales-invoice';
const EMIT_ENDPOINT = '/emit-invoice-payment'; // POST /emit-invoice-payment/:invoice_hash
const CARD_METHOD = 'credit_card_recurring';

// =========================================================
// Tipos
// =========================================================
interface Invoice {
  _id: string;
  installment_number: number;
  payment_date: string;
  installment_value: number;
  payment_status_id: string;
  payment_method_id: string;
  invoice_hash: string;
  project_id?: string;
  client_id?: string;
  client_id_response?: { name?: string };
}

type RowStatus = 'idle' | 'sending' | 'done' | 'error';

// =========================================================
// Helpers
// =========================================================
const formatBRL = (cents: number) =>
  (Number(cents || 0) / 100).toLocaleString('pt-BR', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });

// dd/mm/yyyy a partir dos componentes UTC (faturas gravam meia-noite BRT como 03:00Z).
const formatDateBR = (d: string | Date | null) => {
  if (!d) return '—';
  const dt = d instanceof Date ? d : new Date(d);
  if (isNaN(dt.getTime())) return '—';
  const [y, m, day] = dt.toISOString().slice(0, 10).split('-');
  return `${day}/${m}/${y}`;
};

const invoiceMonth = (d: string) => {
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? '' : dt.toISOString().slice(0, 7); // YYYY-MM (UTC)
};

const currentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const monthLabel = (key: string) => {
  if (!key) return '';
  const [y, m] = key.split('-').map(Number);
  const label = new Date(y, m - 1, 1).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  return label.charAt(0).toUpperCase() + label.slice(1);
};

// =========================================================
// Styled
// =========================================================
const spin = keyframes`from { transform: rotate(0); } to { transform: rotate(360deg); }`;

const Container = styled.div`
  background: #fff; border-radius: 10px; min-width: 360px; max-width: 940px; width: 100%;
  color: #1f2937; font-family: 'Inter', -apple-system, sans-serif;
  display: flex; flex-direction: column; max-height: 80vh;
`;
const Header = styled.div`
  padding: 18px 22px; border-bottom: 1px solid #f3f4f6;
  display: flex; align-items: center; gap: 12px;
`;
const HeaderTitle = styled.div`
  flex: 1;
  h3 { font-size: 16px; font-weight: 800; color: #111827; }
  p { font-size: 12px; color: #6b7280; margin-top: 2px; }
`;
const HeaderClose = styled.button`
  background: none; border: none; cursor: pointer; color: #6b7280;
  display: inline-flex; align-items: center;
  &:hover { color: #333; }
`;
const Body = styled.div`padding: 18px 22px; overflow-y: auto; flex: 1;`;
const Footer = styled.div`
  padding: 14px 22px; border-top: 1px solid #f3f4f6;
  display: flex; gap: 10px; justify-content: flex-end; align-items: center;
`;
const Filters = styled.div`
  display: flex; flex-wrap: wrap; align-items: flex-end; gap: 12px; margin-bottom: 16px;
  .field { display: flex; flex-direction: column; gap: 5px; }
  label { font-size: 11px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.04em; }
  input {
    height: 40px; padding: 0 12px; border: 1.5px solid #e5e7eb; border-radius: 8px;
    font-size: 13px; font-family: inherit; background: #fff;
    &:focus { outline: none; border-color: #333; }
  }
`;
const BtnSearch = styled.button`
  height: 40px; padding: 0 16px; border: none; border-radius: 8px; cursor: pointer;
  background: #333; color: #fff; font-size: 13px; font-weight: 700;
  display: inline-flex; align-items: center; gap: 6px;
  &:disabled { opacity: .5; cursor: not-allowed; }
`;
const SummaryBar = styled.div`
  display: flex; align-items: center; gap: 10px; margin-bottom: 12px;
  span.count { font-size: 12px; color: #6b7280; }
  span.count strong { color: #111827; }
`;
const Table = styled.table`
  width: 100%; border-collapse: collapse; font-size: 12px;
  th {
    text-align: left; padding: 10px 8px; font-size: 11px; font-weight: 600; color: #6b7280;
    text-transform: uppercase; letter-spacing: 0.04em; border-bottom: 1px solid #e5e7eb; background: #fafafa;
  }
  td { padding: 10px 8px; border-bottom: 1px solid #f3f4f6; color: #1f2937; vertical-align: middle; }
  tr.done td { background: rgba(22,163,74,.05); }
  .num { font-variant-numeric: tabular-nums; text-align: right; }
  .muted { color: #9ca3af; font-size: 11px; }
`;
const BtnEmit = styled.button<{ $status: RowStatus }>`
  height: 30px; padding: 0 12px; border-radius: 7px; cursor: pointer; font-size: 12px; font-weight: 700;
  display: inline-flex; align-items: center; gap: 6px; border: 1.5px solid transparent; font-family: inherit;
  ${({ $status }) =>
    $status === 'done'
      ? `background: #f0fdf4; color: #16a34a; border-color: #bbf7d0; cursor: default;`
      : $status === 'error'
      ? `background: #fef2f2; color: #dc2626; border-color: #fecaca;`
      : `background: #333; color: #fff;`}
  &:disabled { opacity: .6; cursor: not-allowed; }
`;
const Empty = styled.div`text-align: center; padding: 40px 0; color: #9ca3af; font-size: 13px;`;
const Spinner = styled.div`
  width: 14px; height: 14px; border: 2px solid #e5e7eb; border-top-color: currentColor;
  border-radius: 999px; animation: ${spin} 1s linear infinite; display: inline-block;
`;
const ProgressInfo = styled.div`
  font-size: 12px; color: #6b7280; flex: 1;
  strong { color: #333; font-weight: 700; }
`;
const BtnCancel = styled.button`
  height: 38px; padding: 0 16px; border: 1.5px solid #e5e7eb; border-radius: 8px; cursor: pointer;
  background: #fff; color: #6b7280; font-size: 13px; font-weight: 600;
  &:disabled { opacity: .5; cursor: not-allowed; }
`;
const BtnPrimary = styled.button`
  height: 38px; padding: 0 18px; border: none; border-radius: 8px; cursor: pointer;
  background: #333; color: #fff; font-size: 13px; font-weight: 700;
  display: inline-flex; align-items: center; gap: 6px;
  &:disabled { opacity: .5; cursor: not-allowed; }
`;

// =========================================================
// Flow
// =========================================================
const EmitCardChargesFlow: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { addToast } = useToast();
  const { handleApiErrors } = useAuth();
  const { reloadSearchAll } = useSearch();

  const [month, setMonth] = useState<string>(currentMonth());
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [rowState, setRowState] = useState<Record<string, RowStatus>>({});

  const [applyingAll, setApplyingAll] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  // ----- buscar faturas de cartão, waiting, do mês -----
  const search = useCallback(async () => {
    if (!month) {
      addToast({ title: 'Selecione o mês', type: 'error' });
      return;
    }
    setLoading(true);
    setSearched(true);
    try {
      const { data } = await api.get(LIST_ENDPOINT, {
        params: {
          limitItems: 1000000,
          where: { payment_status_id: 'waiting', payment_method_id: CARD_METHOD },
        },
      });
      const list: Invoice[] = (data?.rows || []).filter((inv: Invoice) => {
        if (inv.payment_status_id !== 'waiting') return false;      // reforço no front
        if (inv.payment_method_id !== CARD_METHOD) return false;    // reforço no front
        if (!inv.payment_date) return false;
        return invoiceMonth(inv.payment_date) === month;
      });
      list.sort((a, b) =>
        (a.client_id_response?.name || '').localeCompare(b.client_id_response?.name || ''),
      );
      setInvoices(list);
      setRowState({}); // zera estados ao recarregar
    } catch (err) {
      addToast({ title: 'Erro ao carregar cobranças', type: 'error' });
      handleApiErrors(err);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [month, addToast, handleApiErrors]);

  useEffect(() => { search(); }, []); // carrega o mês atual ao abrir

  const totalCents = useMemo(
    () => invoices.reduce((acc, i) => acc + (i.installment_value || 0), 0),
    [invoices],
  );
  const doneCount = useMemo(
    () => Object.values(rowState).filter(s => s === 'done').length,
    [rowState],
  );
  const pendingCount = invoices.length - doneCount;

  // ----- emitir 1 -----
  const emitOne = async (inv: Invoice): Promise<boolean> => {
    if (rowState[inv._id] === 'done' || rowState[inv._id] === 'sending') return false;
    setRowState(s => ({ ...s, [inv._id]: 'sending' }));
    try {
      await api.post(`${EMIT_ENDPOINT}/${inv.invoice_hash}`, { additional_days: '' });
      setRowState(s => ({ ...s, [inv._id]: 'done' }));
      return true;
    } catch (err: any) {
      setRowState(s => ({ ...s, [inv._id]: 'error' }));
      addToast({
        title: err?.response?.data?.message || err?.response?.data?.error || 'Erro ao emitir cobrança',
        type: 'error',
      });
      return false;
    }
  };

  const handleEmitOne = async (inv: Invoice) => {
    const ok = await emitOne(inv);
    if (ok) {
      addToast({ title: 'Cobrança emitida!', type: 'success' });
      reloadSearchAll();
    }
  };

  // ----- emitir todas as pendentes (sequencial) -----
  const emitAll = async () => {
    const pending = invoices.filter(i => rowState[i._id] !== 'done' && rowState[i._id] !== 'sending');
    if (pending.length === 0) return;
    setApplyingAll(true);
    setProgress({ done: 0, total: pending.length });
    let ok = 0;
    let fail = 0;
    for (let i = 0; i < pending.length; i++) {
      const success = await emitOne(pending[i]);
      success ? ok++ : fail++;
      setProgress({ done: i + 1, total: pending.length });
    }
    setApplyingAll(false);
    addToast({
      title: `${ok} cobrança(s) emitida(s)` + (fail ? ` · ${fail} com erro` : ''),
      type: fail ? 'error' : 'success',
    });
    reloadSearchAll();
  };

  return (
    <Container>
      <Header>
        <HeaderTitle>
          <h3>Emitir cobranças no cartão</h3>
          <p>Faturas aguardando, no cartão, com vencimento no mês selecionado (todos os projetos).</p>
        </HeaderTitle>
        <HeaderClose onClick={onClose} disabled={applyingAll}><FiX size={18} /></HeaderClose>
      </Header>

      <Body>
        <Filters>
          <div className="field">
            <label>Mês de vencimento</label>
            <input
              type="month"
              value={month}
              onChange={e => setMonth(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') search(); }}
              disabled={loading || applyingAll}
            />
          </div>
          <BtnSearch onClick={search} disabled={loading || applyingAll}>
            <FiSearch size={14} /> Buscar
          </BtnSearch>
        </Filters>

        {loading ? (
          <Empty><Spinner style={{ color: '#333' }} /> <div style={{ marginTop: 8 }}>Carregando…</div></Empty>
        ) : !searched ? (
          <Empty>Selecione o mês e clique em Buscar.</Empty>
        ) : invoices.length === 0 ? (
          <Empty>Nenhuma cobrança no cartão aguardando em {monthLabel(month)}.</Empty>
        ) : (
          <>
            <SummaryBar>
              <span className="count">
                <strong>{invoices.length}</strong> cobrança(s) · R$ {formatBRL(totalCents)}
                {doneCount > 0 && <> · {doneCount} emitida(s)</>}
              </span>
            </SummaryBar>

            <Table>
              <thead>
                <tr>
                  <th>Nº</th>
                  <th>Cliente</th>
                  <th>Projeto</th>
                  <th>Vencimento</th>
                  <th className="num">Valor</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {invoices.map(inv => {
                  const st = rowState[inv._id] || 'idle';
                  return (
                    <tr key={inv._id} className={st === 'done' ? 'done' : ''}>
                      <td>#{inv.installment_number}</td>
                      <td>{inv.client_id_response?.name || '—'}</td>
                      <td className="muted">{inv.project_id || '—'}</td>
                      <td>{formatDateBR(inv.payment_date)}</td>
                      <td className="num">R$ {formatBRL(inv.installment_value)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <BtnEmit
                          $status={st}
                          onClick={() => handleEmitOne(inv)}
                          disabled={applyingAll || st === 'sending' || st === 'done'}
                        >
                          {st === 'sending' ? (<><Spinner /> Emitindo…</>)
                            : st === 'done' ? (<><FiCheck size={13} /> Emitida</>)
                            : st === 'error' ? (<><FiSend size={13} /> Repetir</>)
                            : (<><FiSend size={13} /> Emitir</>)}
                        </BtnEmit>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </>
        )}
      </Body>

      {searched && invoices.length > 0 && (
        <Footer>
          {applyingAll ? (
            <ProgressInfo>
              <Spinner style={{ color: '#333', verticalAlign: 'middle', marginRight: 6 }} />
              Emitindo <strong>{progress.done}</strong> de <strong>{progress.total}</strong>…
            </ProgressInfo>
          ) : <div style={{ flex: 1 }} />}
          <BtnCancel onClick={search} disabled={loading || applyingAll}>
            <FiRefreshCw size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Recarregar
          </BtnCancel>
          <BtnPrimary onClick={emitAll} disabled={applyingAll || pendingCount === 0}>
            <FiSend size={14} />
            Emitir todas {pendingCount > 0 ? `(${pendingCount})` : ''}
          </BtnPrimary>
        </Footer>
      )}
    </Container>
  );
};

// =========================================================
// Botão exportado (vai no headModules da SalesInvoice)
// =========================================================
export default function EmitCardChargesModule(): JSX.Element {
  const { addModal, removeModal } = useModal();

  const open = () => {
    addModal({
      key: 'emit-card-charges',
      title: '',
      theme: 'whiteModal',
      content: <EmitCardChargesFlow onClose={() => removeModal('emit-card-charges')} />,
    });
  };

  return (
    <button
      className="searchButton"
      title="Emitir cobranças no cartão do mês"
      type="button"
      onClick={open}
    >
      <FaCreditCard />
    </button>
  );
}
